import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Languages } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT } from "@/data/content";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { key: "nav.home", to: "/" },
  { key: "nav.about", to: "/about" },
  { key: "nav.services", to: "/services" },
  { key: "nav.principal", to: "/principal" },
  { key: "nav.physio", to: "/physiotherapy" },
  { key: "nav.doctor", to: "/doctor" },
  { key: "nav.gallery", to: "/gallery" },
  { key: "nav.events", to: "/events" },
  { key: "nav.contact", to: "/contact" },
];

const Navbar = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[color:var(--adhd-border)]"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="adhd-container flex items-center justify-between py-3 lg:py-4">
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
          onClick={() => setOpen(false)}
        >
          <span
            className="grid place-items-center h-10 w-10 rounded-2xl overflow-hidden shadow-sm bg-white"
          >
            <img
              src="/adhd-logo.jpeg"
              alt="ADHD School Bhadrak logo"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif font-semibold text-[15px] text-[color:var(--adhd-text)]">
              ADHD School
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--adhd-muted)]">
              Bhadrak, Odisha
            </span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.key.replace("nav.", "")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-[13px] font-medium transition-colors ${
                  isActive
                    ? "text-[color:var(--adhd-primary)] bg-[color:var(--adhd-bg-soft)]"
                    : "text-[color:var(--adhd-text)]/80 hover:text-[color:var(--adhd-primary)]"
                }`
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher-trigger"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[color:var(--adhd-border)] text-xs font-semibold text-[color:var(--adhd-text)]/80 hover:bg-[color:var(--adhd-bg-soft)] transition-colors"
            >
              <Languages size={14} />
              {languages.find((l) => l.code === lang)?.short}
              <ChevronDown size={12} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`cursor-pointer ${
                    lang === l.code ? "font-semibold text-[color:var(--adhd-primary)]" : ""
                  }`}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            data-testid="nav-call-btn"
            href={`tel:${CONTACT.phoneTel}`}
            className="hidden md:inline-flex adhd-btn-primary !py-2.5 !px-5 !text-xs"
          >
            <Phone size={14} />
            {t("cta.call")}
          </a>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-[color:var(--adhd-border)] hover:bg-[color:var(--adhd-bg-soft)]"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[color:var(--adhd-border)] bg-white/95 backdrop-blur"
        >
          <div className="adhd-container py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.key.replace("nav.", "")}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-sm font-medium ${
                    isActive
                      ? "bg-[color:var(--adhd-bg-soft)] text-[color:var(--adhd-primary)]"
                      : "text-[color:var(--adhd-text)]/90"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 mt-3">
              {languages.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                    lang === l.code
                      ? "bg-[color:var(--adhd-primary)] text-white border-transparent"
                      : "border-[color:var(--adhd-border)] text-[color:var(--adhd-text)]/80"
                  }`}
                >
                  {l.short}
                </button>
              ))}
            </div>
            <a
              data-testid="mobile-call-btn"
              href={`tel:${CONTACT.phoneTel}`}
              className="adhd-btn-primary !py-3 mt-3 !w-full"
            >
              <Phone size={14} />
              {t("cta.call")} · {CONTACT.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
